import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { marketingManifestApi } from "@/network/network";

interface SMMGeneratorContextValue {
  manifestContent: string;
  isLoading: boolean;
  tagReviewOpen: boolean;
  setTagReviewOpen: (open: boolean) => void;
  loadManifest: (opts?: { silent?: boolean }) => Promise<void>;
}

const SMMGeneratorContext = createContext<SMMGeneratorContextValue | undefined>(
  undefined,
);

export const SMMGeneratorProvider = ({ children }: { children: ReactNode }) => {
  const { selectedBoard } = useSwivuBryntumContext();
  const slug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;
  const api = useMemo(() => (slug ? marketingManifestApi(slug) : null), [slug]);

  const [manifestContent, setManifestContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [tagReviewOpen, setTagReviewOpen] = useState(false);

  const loadManifest = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!api) {
        setManifestContent("");
        setIsLoading(false);
        return;
      }
      if (!opts?.silent) setIsLoading(true);
      try {
        const response = await api.get();
        const data = response.data;
        if (data?.detail != null || data?.content == null) {
          setManifestContent("");
          return;
        }
        setManifestContent(data.content || "");
      } catch {
        setManifestContent("");
      } finally {
        setIsLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void loadManifest();
  }, [loadManifest]);

  const value: SMMGeneratorContextValue = useMemo(
    () => ({
      manifestContent,
      isLoading,
      tagReviewOpen,
      setTagReviewOpen,
      loadManifest,
    }),
    [manifestContent, isLoading, tagReviewOpen, loadManifest],
  );

  return (
    <SMMGeneratorContext.Provider value={value}>
      {children}
    </SMMGeneratorContext.Provider>
  );
};

export const useSMMGeneratorContext = () => {
  const context = useContext(SMMGeneratorContext);
  if (context === undefined) {
    throw new Error("useSMMGeneratorContext must be used within SMMGeneratorProvider");
  }
  return context;
};
